/**
 * OpportunityDecisionModal.js
 *
 * Nova oportunidade × mesma oportunidade — a decisão que só o vendedor sabe.
 *
 * POR QUE MODAL E NÃO PUSH
 * ────────────────────────
 * O único push do sistema (`useTaskNotifications`) existe para evento
 * assíncrono: outra pessoa te atribuiu uma tarefa. Aqui o vendedor acabou
 * de clicar em "Montar Cotação" e está olhando para a tela. Notificar
 * alguém sobre a ação que ela mesma disparou é pior que perguntar na hora,
 * e criaria um segundo sistema de notificação para nada.
 *
 * O CONTRATO
 * ──────────
 * O backend responde 409 com `{ destination, open_opportunities[],
 * idempotency_key }` e NÃO grava nada. O vendedor responde, o mesmo
 * payload é reenviado com a decisão, e aí sim a cotação nasce.
 *
 * A `idempotency_key` volta ao servidor no reenvio: é ela que faz duplo
 * clique em "Nova oportunidade" criar uma oportunidade, não duas.
 *
 * MAIS DE UMA OPORTUNIDADE ABERTA
 * ───────────────────────────────
 * Não é escolha binária global. Com Paris e Fortaleza abertas e uma
 * cotação de Roma chegando, "mesma oportunidade" abre a lista para o
 * vendedor apontar QUAL. Nunca escolhemos por ele — foi exatamente esse
 * "escolher a primeira" que `order_resolution.py` existe para impedir.
 */
import { useState } from 'react';
import { colors, borderRadius } from '../styles/tokens';

const fmtValor = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

const fmtData = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d.toLocaleDateString('pt-BR');
};

function OpportunityDecisionModal({ decisao, onDecide, onCancel, busy = false }) {
  const [escolhendo, setEscolhendo] = useState(false);
  const [selecionada, setSelecionada] = useState(null);

  if (!decisao) return null;

  const abertas = decisao.open_opportunities || [];

  // Uma só oportunidade aberta: "mesma oportunidade" já identifica qual.
  // Com várias, a escolha é obrigatória e explícita.
  const escolherMesma = () => {
    if (abertas.length === 1) {
      onDecide({ decision: 'existing', opportunityId: abertas[0].order_id });
      return;
    }
    setEscolhendo(true);
  };

  const confirmarSelecao = () => {
    if (!selecionada) return;
    onDecide({ decision: 'existing', opportunityId: selecionada });
  };

  const rotulo = (opp) => {
    const destinos = (opp.destinations || []).filter(Boolean);
    return destinos.length ? destinos.join(' + ') : 'Oportunidade sem destino';
  };

  return (
    <div style={S.overlay} role="dialog" aria-modal="true">
      <div style={S.card}>
        <div style={S.header}>
          <div style={S.eyebrow}>Nova cotação para</div>
          <div style={S.destino}>{decisao.destination || 'novo destino'}</div>
        </div>

        <p style={S.texto}>
          O cliente já possui {abertas.length === 1
            ? 'uma oportunidade aberta'
            : `${abertas.length} oportunidades abertas`}.
          Essa cotação representa uma nova oportunidade ou é uma alternativa
          da oportunidade existente?
        </p>

        {!escolhendo && (
          <div style={S.botoes}>
            <button
              style={{ ...S.btn, ...S.btnPrimario }}
              disabled={busy}
              onClick={() => onDecide({ decision: 'new' })}
            >
              Nova oportunidade
            </button>
            <button
              style={{ ...S.btn, ...S.btnSecundario }}
              disabled={busy}
              onClick={escolherMesma}
            >
              Mesma oportunidade
            </button>
          </div>
        )}

        {escolhendo && (
          <>
            <div style={S.subtitulo}>
              Qual oportunidade deve receber esta cotação?
            </div>
            <div style={S.lista}>
              {abertas.map((opp) => {
                const ativa = selecionada === opp.order_id;
                const valor = fmtValor(opp.value);
                const data = fmtData(opp.created_at);
                return (
                  <button
                    key={opp.order_id}
                    style={{ ...S.item, ...(ativa ? S.itemAtivo : {}) }}
                    disabled={busy}
                    onClick={() => setSelecionada(opp.order_id)}
                  >
                    <span style={S.itemTitulo}>{rotulo(opp)}</span>
                    <span style={S.itemMeta}>
                      {valor ? `R$ ${valor}` : '—'}
                      {data ? ` · aberta em ${data}` : ''}
                      {opp.destinos_count > 1 ? ` · ${opp.destinos_count} destinos` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={S.botoes}>
              <button
                style={{ ...S.btn, ...S.btnPrimario, ...(selecionada ? {} : S.btnDesabilitado) }}
                disabled={busy || !selecionada}
                onClick={confirmarSelecao}
              >
                Confirmar
              </button>
              <button
                style={{ ...S.btn, ...S.btnSecundario }}
                disabled={busy}
                onClick={() => { setEscolhendo(false); setSelecionada(null); }}
              >
                Voltar
              </button>
            </div>
          </>
        )}

        <button style={S.cancelar} disabled={busy} onClick={onCancel}>
          Cancelar e não criar a cotação
        </button>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(9, 7, 119, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1.5rem',
  },
  card: {
    background: colors.card, borderRadius: borderRadius?.lg || '14px',
    width: '100%', maxWidth: '460px', padding: '1.75rem',
    fontFamily: "'Inter', sans-serif",
    boxShadow: '0 18px 48px rgba(9, 7, 119, 0.28)',
  },
  header: { marginBottom: '0.9rem' },
  eyebrow: {
    fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em',
    color: colors.text.secondary, fontWeight: 600,
  },
  destino: {
    fontSize: '1.35rem', fontWeight: 700,
    color: colors.primary, marginTop: '0.15rem',
  },
  texto: {
    fontSize: '0.92rem', lineHeight: 1.55,
    color: colors.text.primary, margin: '0 0 1.25rem',
  },
  subtitulo: {
    fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.6rem',
    color: colors.text.primary,
  },
  lista: { display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.1rem' },
  item: {
    display: 'flex', flexDirection: 'column', gap: '0.2rem',
    textAlign: 'left', padding: '0.7rem 0.85rem', borderRadius: '9px',
    border: `1.5px solid ${colors.border}`, background: colors.card,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  itemAtivo: {
    borderColor: colors.accent,
    background: colors.accentSoft,
  },
  itemTitulo: {
    fontSize: '0.92rem', fontWeight: 600, color: colors.primary,
  },
  itemMeta: { fontSize: '0.78rem', color: colors.text.secondary },
  botoes: { display: 'flex', gap: '0.6rem' },
  btn: {
    flex: 1, padding: '0.75rem 1rem', borderRadius: '9px',
    fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', border: '1.5px solid transparent',
  },
  btnPrimario: { background: colors.primary, color: colors.card },
  btnSecundario: {
    background: colors.card, color: colors.primary, borderColor: colors.primary,
  },
  btnDesabilitado: { opacity: 0.45, cursor: 'not-allowed' },
  cancelar: {
    display: 'block', width: '100%', marginTop: '0.9rem',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.8rem', color: colors.text.secondary,
    fontFamily: 'inherit', padding: '0.4rem',
  },
};

export default OpportunityDecisionModal;
